import React, { useContext, useEffect, useState } from "react";

import {
  Button,
  Modal,
  StepperDispatchContext,
  StepperStateContext,
  SuccessModalContent,
  Tooltip,
} from "@king-ict/eupisi-ui/components";
import { useBreakpoint } from "@king-ict/eupisi-ui/hooks";
import {
  KindergartenRoleEnum,
  RequestStatusEnum,
} from "@king-ict/eupisi-ui/models";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { useRouteMatch } from "react-router";
import { v4 as uuidv4 } from "uuid";

import {
  useCreateDraftRequestMutation,
  useUpdateDraftRequestMutation,
} from "../../api/requestApi";
import { useTypedSelector } from "../../app/rootReducer";
import { useAppDispatch } from "../../app/store";

import { useCurrentUser } from "../../hooks/authorization/useCurrentUser";
import EnrollmentTypeEnum from "../../models/EnrollmentType/EnrollmentTypeEnum";
import RequestTypeEnum from "../../models/Request/RequestTypeEnum";
import {
  adminRequestsUrl,
  adminSubsequentRequestsUrl,
  dashboard as dashboardRoute,
  adminPreschoolRequestsUrl,
  draftRequestUrl,
  draftPreschoolRequestUrl,
  newPreschoolRequestRoute,
  draftPreschoolRequestRoute,
  editPreschoolRequestRoute,
  draftPreschoolRequestAsAdminUrl,
  draftRequestAsAdminUrl,
} from "../../routes/routes";
import { clearRequestData, setRequestUID } from "../../slices/requestSlice";
import styles from "./RequestStepperHeaderButtons.module.scss";

interface Props {
  currentStep: number;
  currentSubstep?: number;
}

export const RequestStepperHeaderButtons = ({
  currentStep,
  currentSubstep,
}: Props) => {
  const request = useTypedSelector((state) => state.request.request);
  const user = useCurrentUser();
  const pedagogicalYear = useTypedSelector((state) => state.pedagogicalYear);
  const stepperStateContext = useContext(StepperStateContext);
  const stepperDispatch = useContext(StepperDispatchContext);
  const { t } = useTranslation();
  const { guid, requestId } = useParams<{ guid: string; requestId: string }>();
  const dispatch = useAppDispatch();

  const newPreschoolRequestMatch = useRouteMatch(newPreschoolRequestRoute());
  const draftPreschoolRequestMatch = useRouteMatch(
    draftPreschoolRequestRoute()
  );
  const editPreschoolRequestMatch = useRouteMatch(editPreschoolRequestRoute());

  const history = useHistory();

  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [
    openRequestDraftSuccessModal,
    setOpenRequestDraftSuccessModal,
  ] = useState(false);

  const [openDraftModal, setOpenDraftModal] = useState(false);

  const [save, setSave] = useState(false);
  const [shouldClose, setShouldClose] = useState(false);
  const [
    createDraftRequest,
    { isLoading: isCreatingDraftRequest },
  ] = useCreateDraftRequestMutation();
  const [
    updateDraftRequest,
    { isLoading: isUpdatingDraftRequest },
  ] = useUpdateDraftRequestMutation();
  const isSavingDraft = isCreatingDraftRequest || isUpdatingDraftRequest;

  useEffect(() => {
    if (!save) {
      return;
    }

    setSave(false);

    (async () => {
      try {
        if (guid) {
          await updateDraftRequest({ request, guid }).unwrap();
          handleToggleDraftModal();
          handleToggleRequestDraftSuccessModal();
          return;
        }

        const uid = uuidv4();
        const requestNewUID = { ...request, uId: uid };
        await createDraftRequest({
          request: requestId ? requestNewUID : request,
          requestId,
        }).unwrap();

        if (requestId) {
          dispatch(setRequestUID(uid));
        }

        handleToggleDraftModal();
        handleToggleRequestDraftSuccessModal();
      } catch (error) {
        // Global API interceptor shows request errors.
      }
    })();
  }, [
    createDraftRequest,
    dispatch,
    save,
    guid,
    request,
    requestId,
    updateDraftRequest,
  ]);

  const sizeBreakpoint = useBreakpoint();

  const handleToggleCancelModal = () => {
    setOpenCancelModal((openCancelModal) => !openCancelModal);
  };

  const handleToggleDraftModal = () => {
    setOpenDraftModal((openDraftModal) => !openDraftModal);
  };

  const handleToggleRequestDraftSuccessModal = () => {
    setOpenRequestDraftSuccessModal(
      (openRequestDraftSuccessModal) => !openRequestDraftSuccessModal
    );
  };

  const handleRequeststDraftSuccessConfirmed = () => {
    handleToggleRequestDraftSuccessModal();
    if (shouldClose) {
      dispatch(clearRequestData());
      if (user.institutionId && user.roleId !== KindergartenRoleEnum.Parent) {
        window.location.href =
          newPreschoolRequestMatch != null ||
          editPreschoolRequestMatch != null ||
          draftPreschoolRequestMatch != null
            ? adminPreschoolRequestsUrl()
            : pedagogicalYear.currentImportantDate.enrollmentTypeId ===
              EnrollmentTypeEnum.SubsequentEnrollment
            ? adminSubsequentRequestsUrl()
            : adminRequestsUrl();
      } else {
        history.push(dashboardRoute());
      }
    } else if (!guid) {
      if (user.institutionId && user.roleId !== KindergartenRoleEnum.Parent) {
        history.push(
          request.requestTypeId === RequestTypeEnum.Preschool
            ? draftPreschoolRequestAsAdminUrl(
                request.uId,
                request.applicantParent.oib
              )
            : draftRequestAsAdminUrl(request.uId, request.applicantParent.oib)
        );
      } else {
        history.push(
          request.requestTypeId === RequestTypeEnum.Preschool
            ? draftPreschoolRequestUrl(request.uId)
            : draftRequestUrl(request.uId)
        );
      }
    }
  };

  const canCreateDraft =
    (currentStep === 2 && currentSubstep === 1) || currentStep > 2;

  const handleClose = () => {
    stepperStateContext &&
      stepperStateContext.close &&
      stepperStateContext.close();

    stepperDispatch && stepperDispatch({ type: "close", payload: undefined });

    handleToggleCancelModal();
    dispatch(clearRequestData());
    if (user.roleId !== KindergartenRoleEnum.Parent) {
      window.location.href =
        newPreschoolRequestMatch != null ||
        editPreschoolRequestMatch != null ||
        draftPreschoolRequestMatch != null
          ? adminPreschoolRequestsUrl()
          : pedagogicalYear.currentImportantDate.enrollmentTypeId ===
            EnrollmentTypeEnum.SubsequentEnrollment
          ? adminSubsequentRequestsUrl()
          : adminRequestsUrl();
    } else {
      history.push(dashboardRoute());
    }
  };

  const handleSave = async () => {
    if (isSavingDraft) {
      return;
    }

    stepperStateContext &&
      stepperStateContext.save &&
      stepperStateContext.save();

    stepperDispatch && stepperDispatch({ type: "save", payload: undefined });

    setSave(true);
  };

  const handleSaveWithoutClose = async () => {
    setShouldClose(false);
    handleToggleDraftModal();
  };

  const handleSaveAndCloseClicked = () => {
    setShouldClose(true);
    handleToggleDraftModal();
  };

  const renderButton = (button: any) => {
    if (sizeBreakpoint === "xs" || sizeBreakpoint === "sm") {
      return (
        <Row className={styles.mobileButtonRow}>
          <Col xs={12}>{button}</Col>
        </Row>
      );
    }
    return button;
  };

  return (
    <>
      {renderButton(
        <Button outline onClick={handleToggleCancelModal}>
          {t("request.closeRequestButton")}
        </Button>
      )}

      {request.status.id !== RequestStatusEnum.Unlocked &&
        request.status.id !== RequestStatusEnum.Created &&
        (canCreateDraft ? (
          <>
            {renderButton(
              <Button disabled={isSavingDraft} onClick={handleSaveWithoutClose}>
                {t("request.saveRequestButton")}
              </Button>
            )}
            {renderButton(
              <Button
                disabled={isSavingDraft}
                onClick={handleSaveAndCloseClicked}
              >
                {t("request.saveAndCloseRequestButton")}
              </Button>
            )}
          </>
        ) : (
          <>
            <Tooltip
              overlay={<span>{t("request.saveRequestButtonTooltip")}</span>}
              trigger={"hover"}
            >
              <span className={styles.disabledButtonWrapper}>
                {renderButton(
                  <Button onClick={() => {}} disabled>
                    {t("request.saveRequestButton")}
                  </Button>
                )}
              </span>
            </Tooltip>
            <Tooltip
              overlay={
                <span>{t("request.saveAndCloseRequestButtonTooltip")}</span>
              }
              trigger={"hover"}
            >
              <span className={styles.disabledButtonWrapper}>
                {renderButton(
                  <Button onClick={() => {}} disabled>
                    {t("request.saveAndCloseRequestButton")}
                  </Button>
                )}
              </span>
            </Tooltip>
          </>
        ))}

      <Modal
        type={"confirm"}
        title={t("request.closeConfirmationTitle")}
        open={openCancelModal}
        confirmText={t("request.closeConfirmationYes")}
        cancelText={t("common.no")}
        onClose={handleToggleCancelModal}
        onConfirm={handleClose}
      >
        <div>{t("request.closeConfirmationMessage")}</div>
      </Modal>

      <Modal
        type={"confirm"}
        title={t("request.draftConfirmationTitle")}
        open={openDraftModal}
        confirmText={t("request.draftConfirmationYes")}
        cancelText={t("common.no")}
        onClose={handleToggleDraftModal}
        onConfirm={handleSave}
      >
        <div>{t("request.draftConfirmationMessage")}</div>
        {request.status.id === RequestStatusEnum.Created && (
          <div>
            <strong>{t("request.draftConfirmationMessageWarning")}: </strong>
            {t("request.draftConfirmationMessageWarningText")}
          </div>
        )}
      </Modal>

      <Modal
        type={"message"}
        open={openRequestDraftSuccessModal}
        confirmText={t("common.close")}
        onConfirm={handleRequeststDraftSuccessConfirmed}
        onClose={() => handleToggleRequestDraftSuccessModal()}
      >
        <SuccessModalContent
          message={t("requestDetails.draftRequestSuccessfullMessage")}
        />
      </Modal>
    </>
  );
};
